import { useState, useEffect } from 'react';
import { getBackgroundImages } from 'src/services/service-handlers/PublicService';
import { getBackgroundImageUrls } from 'src/utils/imageUtils';

const CACHE_KEY = 'bg_images_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface BackgroundImages {
  bg_image?: string;
}

interface CacheData {
  images: BackgroundImages;
  rawValue?: string; // Store the raw JSON string from API for comparison
  blobData?: string; // Store blob as base64 data URL so we can recreate blob URL without API call
  timestamp: number;
}

const defaultImage = '/assets/background/mountains_bg_light.png';

const defaultImages: BackgroundImages = {
  bg_image: defaultImage,
};

export function getCachedBgImages(): BackgroundImages | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed: CacheData = JSON.parse(cached);
      // Check if cache is still valid (within cache duration)
      if (parsed.timestamp && Date.now() - parsed.timestamp < CACHE_DURATION) {
        // If we have blob data (base64), recreate blob URL from it
        if (parsed.blobData && parsed.images.bg_image) {
          // Blob data is stored as base64 data URL, use it directly
          return {
            bg_image: parsed.blobData, // This is a data URL, can be used directly
          };
        }
        // Handle migration from old format (bg_light/bg_dark) to new format (bg_image)
        if (parsed.images.bg_image) {
          return parsed.images;
        }
        // If old format exists, use bg_dark or bg_light as the single image
        if ((parsed.images as any).bg_dark || (parsed.images as any).bg_light) {
          return {
            bg_image: (parsed.images as any).bg_dark || (parsed.images as any).bg_light || defaultImage,
          };
        }
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null;
}

export function setCachedBgImages(images: BackgroundImages, rawValue?: string | null, blobData?: string): void {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        images,
        rawValue: rawValue || undefined, // Store raw JSON string for comparison (convert null to undefined)
        blobData: blobData || undefined, // Store blob as base64 data URL
        timestamp: Date.now(),
      })
    );
  } catch (e) {
    // Ignore cache errors
  }
}

export function useBackgroundImages() {
  const [bgImages, setBgImages] = useState<BackgroundImages>(() => {
    const cached = getCachedBgImages();
    // Always return valid images (cached or defaults)
    return cached || defaultImages;
  });

  const [isLoading, setIsLoading] = useState(() => {
    // Only show loader if there's no cache (first load)
    return getCachedBgImages() === null;
  });

  useEffect(() => {
    const cached = getCachedBgImages();
    
    // Always fetch from API to get latest image
    // If cache exists, use it immediately but still fetch in background
    if (cached) {
      setBgImages(cached);
      setIsLoading(false);
    } else {
      // No cache, show loader on first load
      setIsLoading(true);
    }

    // Always fetch from API to check for changes
    const fetchBackgroundImages = async () => {
      try {
        const response = await getBackgroundImages();
        
        let imageValue: string | null | undefined = null;
        
        if (response && response.data) {
          // Handle case where response is wrapped in data property
          const responseData = response.data;
          imageValue = responseData.bg_image || response.bg_image || responseData.bg_dark || responseData.bg_light || response.bg_dark || response.bg_light;
        } else if (response) {
          // Direct response without data wrapper
          imageValue = response.bg_image || response.bg_dark || response.bg_light;
        }
        
        // Check if image was deleted (empty string, null, or undefined)
        const isImageDeleted = !imageValue || imageValue === '' || imageValue === null;
        
        if (isImageDeleted) {
          // User removed image - clear cache and show default
          try {
            localStorage.removeItem(CACHE_KEY);
          } catch (e) {
            // Ignore cache errors
          }
          setBgImages(defaultImages);
          setIsLoading(false);
          return;
        }
        
        // Get cached raw value and blob data for comparison
        const currentCached = getCachedBgImages();
        let cachedRawValue: string | undefined;
        let cachedBlobData: string | undefined;
        try {
          const cachedData = localStorage.getItem(CACHE_KEY);
          if (cachedData) {
            const parsed: CacheData = JSON.parse(cachedData);
            cachedRawValue = parsed.rawValue;
            cachedBlobData = parsed.blobData;
          }
        } catch (e) {
          // Ignore
        }
        
        // Compare raw JSON strings to detect if image changed
        const rawValueChanged = cachedRawValue !== imageValue;
        // Check if we need to fetch blob (either value changed or we don't have blob data in cache)
        const needsBlobFetch = rawValueChanged || !cachedBlobData;
        
        if (needsBlobFetch) {
          // Image changed OR we don't have blob data - process and update
          const imageUrls = await getBackgroundImageUrls({
            bg_image: imageValue,
          });
          
          const finalImageUrl = imageUrls.bg_image || imageValue || defaultImage;
          
          // Convert blob URL to base64 data URL for caching (so we can display without API call)
          let blobDataUrl: string | undefined;
          if (finalImageUrl && finalImageUrl.startsWith('blob:')) {
            try {
              // Fetch the blob from blob URL and convert to base64 data URL
              const blobResponse = await fetch(finalImageUrl);
              const blob = await blobResponse.blob();
              blobDataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch (e) {
              // If conversion fails, just use the blob URL (will need API call next time)
              blobDataUrl = finalImageUrl;
            }
          } else {
            // Not a blob URL (regular URL or default), use as-is
            blobDataUrl = finalImageUrl;
          }
          
          const finalImages: BackgroundImages = {
            bg_image: blobDataUrl || finalImageUrl, // Use data URL if available, otherwise blob URL
          };
          
          // Update state with new processed image
          setBgImages(finalImages);
          
          // Cache processed data URL, raw JSON string, and blob data
          if (blobDataUrl && blobDataUrl !== defaultImage && !blobDataUrl.startsWith('/assets/background/')) {
            setCachedBgImages(finalImages, imageValue || undefined, blobDataUrl); // Store raw value and blob data
          } else {
            // If it's default image, clear cache
            try {
              localStorage.removeItem(CACHE_KEY);
            } catch (e) {
              // Ignore cache errors
            }
          }
        } else if (currentCached) {
          // Raw value is same AND we have blob data - keep using cached blob/data URL
          // The cached image is already displayed from initial state (from blobData)
          setBgImages(currentCached);
        }
      } catch (error) {
        // Fallback to defaults if API fails
        setBgImages(defaultImages);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBackgroundImages();
  }, []);

  // Ensure we always return valid image
  const finalImage: BackgroundImages = {
    bg_image: bgImages?.bg_image || defaultImage,
  };

  return { bgImages: finalImage, isLoading };
}

